/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { When } from "react-if";
import { Button, ModalConfirm, Text, theme } from "@nordcloud/gnui";
import { useAddSandboxMutation } from "~/generated/graphql";
import { ERROR_TEXT, SUCCESS_TEXT } from "~/constants";
import { useFeature } from "~/hooks/useFeature";
import { useCustomers } from "~/services/customers";
import { showError, showSuccess } from "~/services/toast";
import { isNotEmpty } from "~/tools";

export function AddSandbox() {
  const [isModalConfirmOpen, setIsModalConfirmOpen] = React.useState(false);
  const [isModalSuccessOpen, setIsModalSuccessOpen] = React.useState(false);

  const { user } = useAuth0();
  const [addSandbox, { data }] = useAddSandboxMutation();

  const useExperimental = useFeature("allowExperimental");
  const { selectedCustomer, customers } = useCustomers();

  const isSandbox = selectedCustomer?.name.toLowerCase().includes("sandbox");
  const hasSandbox = customers
    .map((item) => item.name)
    .includes(`Sandbox - ${selectedCustomer?.name}`);

  const onAddSandboxHandler = async () => {
    try {
      await addSandbox({
        variables: { input: { userEmail: user?.email ?? "" } },
      });
      showSuccess(SUCCESS_TEXT.sandboxCreated());
      setIsModalConfirmOpen(false);
    } catch {
      showError(ERROR_TEXT.default);
    }
  };

  React.useEffect(() => {
    if (isNotEmpty(data?.addSandbox?.id ?? "")) {
      setIsModalSuccessOpen(true);
    }
  }, [data]);

  return (
    <When condition={useExperimental && !isSandbox && !hasSandbox}>
      <Button
        mr={theme.spacing.spacing04}
        onClick={() => setIsModalConfirmOpen(true)}
      >
        Create Sandbox
      </Button>
      <ModalConfirm
        isOpen={isModalConfirmOpen}
        confirm={onAddSandboxHandler}
        actionLabel="Create"
        contentLabel="Create sandbox"
        onClose={() => setIsModalConfirmOpen(false)}
      >
        <Text>Do you want to create sandbox for {user?.email}?</Text>
      </ModalConfirm>
      <ModalConfirm
        isOpen={isModalSuccessOpen}
        confirm={openDocs}
        actionLabel="Show Documentation"
        contentLabel="Success"
        onClose={() => setIsModalSuccessOpen(false)}
      >
        <Text>
          Your sandbox was created successfully. Do you want to see
          documentation?
        </Text>
      </ModalConfirm>
    </When>
  );
}

const openDocs = () =>
  window.open(
    "https://docs.mca.nordcloudapp.com/docs/operations/external-integrations/sandbox/",
    "_blank",
    "noreferrer"
  );
